import React, { useState, useRef } from 'react';
import {
  Card, CardContent, Typography, Grid, Button, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Box, useTheme, Zoom,
  ButtonGroup, Menu, MenuItem, ListItemIcon, ListItemText, Divider, Tooltip
} from "@mui/material";
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SettingsBackupRestoreRoundedIcon from '@mui/icons-material/SettingsBackupRestoreRounded';
import LayersClearRoundedIcon from '@mui/icons-material/LayersClearRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from "react-i18next";
import { LiveBadge } from "../../../components/states";

const TrainingControlPanel = ({
  colors,
  currentConfig,
  segmentationLoading,
  configLoading,
  isRunning,
  isCompleted,
  logs,
  isClearing,
  handleStartTraining,
  handleStop,
  handleClear,
  handleClearConfig,
  handleClearAll,
  onShowAllExecutions,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [stopDialogOpen, setStopDialogOpen] = useState(false);

  // Clear dropdown state
  const clearAnchorRef = useRef(null);
  const [clearMenuOpen, setClearMenuOpen] = useState(false);

  // Confirmation state for the destructive clear actions
  const [clearConfirm, setClearConfirm] = useState({ open: false, kind: null });

  const isCurrentlyRunning = isRunning && !isCompleted;
  const hasConfig = !!currentConfig;
  const hasSessionData = (logs || []).length > 0 || isCompleted;
  const startDisabled = isCurrentlyRunning || !hasConfig || segmentationLoading;

  // Stop dialog
  const handleOpenStopDialog = () => setStopDialogOpen(true);
  const handleCloseStopDialog = () => setStopDialogOpen(false);
  const handleConfirmStop = () => {
    handleStop();
    handleCloseStopDialog();
  };

  // Clear dropdown
  const handleClearMenuToggle = () => setClearMenuOpen((open) => !open);
  const handleClearMenuClose = () => setClearMenuOpen(false);

  const requestClear = (kind) => {
    handleClearMenuClose();
    setClearConfirm({ open: true, kind });
  };
  const cancelClear = () => setClearConfirm({ open: false, kind: null });
  const confirmClear = async () => {
    const kind = clearConfirm.kind;
    setClearConfirm({ open: false, kind: null });
    if (window.clearSegmentationCache) {
      try { window.clearSegmentationCache(); } catch (_) {}
    }
    if (kind === 'session') {
      if (typeof handleClear === 'function') await handleClear();
    } else if (kind === 'config') {
      if (typeof handleClearConfig === 'function') {
        await handleClearConfig();
      }
    } else if (kind === 'all') {
      if (typeof handleClearAll === 'function') {
        await handleClearAll();
      } else {
        if (typeof handleClear === 'function') await handleClear();
        if (typeof handleClearConfig === 'function') await handleClearConfig();
      }
    }
  };

  // Quick action for the main split button — fires the most-common case
  // (clear current session) without opening the menu. The chevron next to it
  // is the way into the per-target options.
  const handleQuickClear = () => {
    if (!hasSessionData && hasConfig) {
      // If there's no session to clear but a config exists, open the menu so
      // the user can pick "Clear configuration" deliberately rather than
      // surprising them with a no-op or accidental config wipe.
      setClearMenuOpen(true);
      return;
    }
    requestClear('session');
  };

  // Compact pro button base
  const commonBtnStyle = {
    borderRadius: 1.25,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.8125rem',
    py: 0.75,
    px: 1.75,
    minHeight: 36,
    transition: 'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
  };

  const clearMenuItems = [
    {
      key: 'session',
      label: 'Clear session',
      caption: 'Logs, results & training state',
      Icon: RestartAltRoundedIcon,
      disabled: !hasSessionData || isCurrentlyRunning || isClearing,
    },
    {
      key: 'config',
      label: 'Clear configuration',
      caption: 'Saved config & feature selection',
      Icon: SettingsBackupRestoreRoundedIcon,
      disabled: !hasConfig || isCurrentlyRunning || isClearing,
    },
    {
      key: 'all',
      label: 'Clear everything',
      caption: 'Session and configuration',
      Icon: LayersClearRoundedIcon,
      disabled: (!hasConfig && !hasSessionData) || isCurrentlyRunning || isClearing,
      destructive: true,
    },
  ];

  // Confirmation modal copy
  const confirmCopy = (() => {
    if (clearConfirm.kind === 'session') {
      return {
        title: 'Clear current session?',
        body: 'This removes the current logs, results and training state. Your saved configuration will be kept so you can launch another run right away.',
        action: 'Clear session',
        color: '#0087B4',
      };
    }
    if (clearConfirm.kind === 'config') {
      return {
        title: 'Clear saved configuration?',
        body: 'This removes the saved configuration and feature selection. You will need to set up a new configuration on the Configurations page before training can start. Current session logs will be kept.',
        action: 'Clear configuration',
        color: '#d97706',
      };
    }
    if (clearConfirm.kind === 'all') {
      return {
        title: 'Clear everything?',
        body: 'This removes both the current session (logs, results, training state) AND the saved configuration. You will need to set up a fresh configuration before launching another run.',
        action: 'Clear everything',
        color: '#dc2626',
      };
    }
    return { title: '', body: '', action: '', color: '#0087B4' };
  })();

  // Start-button tooltip explains the disabled state for a data-scientist user.
  const startTooltip = (() => {
    if (isCurrentlyRunning) return 'A training run is already in progress.';
    if (segmentationLoading) return 'Loading segmentation prerequisites…';
    if (!hasConfig) return 'No configuration saved yet, set one on the Configurations page first.';
    return '';
  })();

  return (
    <>
      <Card elevation={0} sx={{
        mb: 2,
        backgroundColor: 'var(--fx-paper)',
        borderRadius: 2,
        border: '1px solid var(--fx-border)',
        boxShadow: 'var(--fx-shadow-sm)',
      }}>
        <CardContent sx={{ p: '16px 18px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 26, height: 26, borderRadius: 1,
                bgcolor: 'rgba(0,135,180,0.10)', color: '#0087B4',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <PlayCircleFilledIcon sx={{ fontSize: 16 }} />
              </Box>
              <Typography sx={{ color: 'var(--fx-text-primary)', fontWeight: 700, fontSize: '0.9375rem', letterSpacing: '-0.005em' }}>
                {t('execute.trainingControls')}
              </Typography>
            </Box>
            {/* Tiny state hint — kept compact so it complements (not duplicates) the LaunchStatusBanner above */}
            {isCurrentlyRunning && <LiveBadge label="Live" />}
          </Box>

          <Grid container spacing={1} alignItems="center">
            {/* START BUTTON — wrapped in Tooltip so disabled state still gets a hint */}
            <Grid item>
              <Tooltip title={startTooltip} arrow placement="top" disableHoverListener={!startTooltip}>
                <span>
                  <Button
                    variant="contained"
                    disableElevation
                    startIcon={
                      isCurrentlyRunning
                        ? <CircularProgress size={14} thickness={5} sx={{ color: '#ffffff' }} />
                        : <PlayArrowRoundedIcon sx={{ fontSize: 18 }} />
                    }
                    onClick={() => {
                      if (window.clearSegmentationCache) window.clearSegmentationCache();
                      handleStartTraining();
                    }}
                    disabled={startDisabled}
                    sx={{
                      ...commonBtnStyle,
                      background: `linear-gradient(135deg, #0087B4 0%, #1B365D 100%)`,
                      color: '#ffffff',
                      boxShadow: '0 2px 6px rgba(0,135,180,0.25)',
                      '&.Mui-disabled': {
                        background: isCurrentlyRunning
                          ? `linear-gradient(135deg, #0087B4 0%, #1B365D 100%)`
                          : 'var(--fx-border)',
                        color: isCurrentlyRunning ? 'var(--fx-paper)' : 'var(--fx-text-muted)',
                      },
                      '&:hover': {
                        background: 'linear-gradient(135deg, #0087B4 0%, #132643 100%)',
                        boxShadow: '0 4px 10px rgba(0,135,180,0.32)',
                      }
                    }}
                  >
                    {segmentationLoading || configLoading ? t('execute.loading') : isCurrentlyRunning ? 'Running…' : t('execute.start')}
                  </Button>
                </span>
              </Tooltip>
            </Grid>

            {/* STOP BUTTON (Triggers Modal) */}
            <Grid item>
              <Button
                variant="contained"
                disableElevation
                startIcon={<StopRoundedIcon sx={{ fontSize: 16 }} />}
                onClick={handleOpenStopDialog}
                disabled={!isRunning || isCompleted}
                sx={{
                  ...commonBtnStyle,
                  backgroundColor: 'rgba(220, 38, 38, 0.08)',
                  color: '#dc2626',
                  border: '1px solid rgba(220, 38, 38, 0.25)',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(220, 38, 38, 0.14)',
                    border: '1px solid #dc2626',
                  },
                  '&:disabled': {
                    backgroundColor: 'var(--fx-subtle)',
                    color: 'var(--fx-border-strong)',
                    border: '1px solid var(--fx-border)',
                  },
                }}
              >
                {t('execute.stop')}
              </Button>
            </Grid>

            {/* CLEAR SPLIT BUTTON — main action (Clear session) + dropdown for the other targets */}
            <Grid item>
              <ButtonGroup
                ref={clearAnchorRef}
                variant="text"
                disableElevation
                sx={{
                  border: '1px solid var(--fx-border)',
                  borderRadius: 1.25,
                  bgcolor: 'var(--fx-paper)',
                  overflow: 'hidden',
                  '& .MuiButtonGroup-grouped': {
                    border: 'none',
                    minHeight: 36,
                  },
                  '& .MuiButtonGroup-grouped:not(:last-of-type)': {
                    borderRight: '1px solid var(--fx-border)',
                  },
                }}
              >
                <Button
                  onClick={handleQuickClear}
                  disabled={(!hasSessionData && !hasConfig) || isCurrentlyRunning || isClearing}
                  startIcon={isClearing
                    ? <CircularProgress size={14} color="inherit" />
                    : <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    ...commonBtnStyle,
                    color: 'var(--fx-text-secondary)',
                    px: 1.5,
                    '&:hover': { backgroundColor: 'rgba(15,23,42,0.05)', color: 'var(--fx-text-primary)' },
                    '&.Mui-disabled': { color: 'var(--fx-border-strong)' },
                  }}
                >
                  {isClearing ? t('execute.clearing') : 'Clear'}
                </Button>
                <Button
                  onClick={handleClearMenuToggle}
                  disabled={isCurrentlyRunning || isClearing}
                  aria-haspopup="menu"
                  aria-expanded={clearMenuOpen ? 'true' : undefined}
                  aria-controls={clearMenuOpen ? 'clear-menu' : undefined}
                  sx={{
                    ...commonBtnStyle,
                    minWidth: 30,
                    px: 0.5,
                    color: 'var(--fx-text-secondary)',
                    '&:hover': { backgroundColor: 'rgba(15,23,42,0.05)', color: 'var(--fx-text-primary)' },
                    '&.Mui-disabled': { color: 'var(--fx-border-strong)' },
                  }}
                >
                  <ArrowDropDownRoundedIcon sx={{ fontSize: 22 }} />
                </Button>
              </ButtonGroup>

              <Menu
                id="clear-menu"
                anchorEl={clearAnchorRef.current}
                open={clearMenuOpen}
                onClose={handleClearMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                  paper: {
                    elevation: 0,
                    sx: {
                      mt: 1,
                      minWidth: 280,
                      backgroundColor: 'var(--fx-paper)',
                      color: 'var(--fx-text-primary)',
                      border: '1px solid var(--fx-border)',
                      borderRadius: 1.5,
                      boxShadow: 'var(--fx-shadow-xl)',
                      overflow: 'hidden',
                    },
                  },
                }}
                MenuListProps={{ dense: true, sx: { py: 0.5 } }}
              >
                <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
                  <Typography sx={{ color: 'var(--fx-text-muted)', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    Clear scope
                  </Typography>
                </Box>
                {clearMenuItems.map((item, idx) => {
                  const ItemIcon = item.Icon;
                  return (
                    <React.Fragment key={item.key}>
                      {idx === clearMenuItems.length - 1 && (
                        <Divider sx={{ my: 0.5, borderColor: 'var(--fx-border)' }} />
                      )}
                      <MenuItem
                        onClick={() => requestClear(item.key)}
                        disabled={item.disabled}
                        sx={{
                          py: 1,
                          alignItems: 'flex-start',
                          gap: 1,
                          color: item.destructive ? 'var(--fx-danger-text)' : 'var(--fx-text-primary)',
                          '&:hover': {
                            backgroundColor: item.destructive ? 'var(--fx-danger-soft)' : 'var(--fx-info-soft)',
                          },
                          '&.Mui-disabled': { opacity: 0.45 },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: '0 !important', mr: 0.5, color: 'inherit' }}>
                          <ItemIcon sx={{ fontSize: 18 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          secondary={item.caption}
                          primaryTypographyProps={{
                            sx: { fontSize: '0.8125rem', fontWeight: 600, color: 'inherit', lineHeight: 1.25 },
                          }}
                          secondaryTypographyProps={{
                            sx: { fontSize: '0.7rem', color: 'var(--fx-text-secondary)', mt: 0.1 },
                          }}
                        />
                      </MenuItem>
                    </React.Fragment>
                  );
                })}
              </Menu>
            </Grid>

            <Grid item xs />

            {/* HISTORY BUTTON */}
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<HistoryRoundedIcon sx={{ fontSize: 16 }} />}
                onClick={onShowAllExecutions}
                sx={{
                  ...commonBtnStyle,
                  borderColor: 'var(--fx-border)',
                  color: 'var(--fx-text-primary)',
                  '&:hover': {
                     borderColor: '#0087B4',
                     backgroundColor: 'rgba(0,135,180,0.06)',
                  }
                }}
              >
                Execution history
              </Button>
            </Grid>
          </Grid>

          {/* Helper row: only render when start is gated by missing config — gives the user a clear next step */}
          {!isCurrentlyRunning && !hasConfig && (
            <Box
              sx={{
                mt: 1.5,
                px: 1.25,
                py: 0.9,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.75,
                border: '1px dashed var(--fx-border)',
                borderRadius: 1,
                backgroundColor: 'var(--fx-subtle)',
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16, color: '#0087B4', mt: '1px' }} />
              <Typography sx={{ color: 'var(--fx-text-secondary)', fontSize: '0.75rem', lineHeight: 1.5 }}>
                No configuration saved yet. Set one on the{' '}
                <Box
                  component="span"
                  sx={{ color: '#0087B4', fontWeight: 700, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => { window.location.hash = ''; window.location.pathname = '/configs'; }}
                >
                  Configurations page
                </Box>{' '}
                before launching a training run.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* STOP CONFIRMATION MODAL */}
      <Dialog
        open={stopDialogOpen}
        onClose={handleCloseStopDialog}
        TransitionComponent={Zoom}
        PaperProps={{
          sx: { borderRadius: '20px', p: 1, width: '400px' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ p: 1, bgcolor: 'var(--fx-danger-soft)', borderRadius: '12px', display: 'flex' }}>
            <WarningAmberRoundedIcon sx={{ color: 'var(--fx-danger)' }} />
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.25rem' }}>
            Confirm Termination
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--fx-text-secondary)', fontSize: '0.95rem' }}>
            Are you sure you want to stop the current training session? All unsaved progress for this round will be lost.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleCloseStopDialog}
            sx={{ ...commonBtnStyle, color: 'var(--fx-text-secondary)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmStop}
            variant="contained"
            disableElevation
            sx={{
              ...commonBtnStyle,
              bgcolor: '#ef4444',
              '&:hover': { bgcolor: '#dc2626' }
            }}
          >
            Stop Training
          </Button>
        </DialogActions>
      </Dialog>

      {/* CLEAR CONFIRMATION MODAL */}
      <Dialog
        open={clearConfirm.open}
        onClose={cancelClear}
        TransitionComponent={Zoom}
        PaperProps={{ sx: { borderRadius: '16px', p: 1, width: '440px' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ p: 1, bgcolor: `${confirmCopy.color}14`, color: confirmCopy.color, borderRadius: '12px', display: 'flex' }}>
            <DeleteSweepOutlinedIcon />
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
            {confirmCopy.title}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--fx-text-secondary)', fontSize: '0.875rem', lineHeight: 1.55 }}>
            {confirmCopy.body}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={cancelClear} sx={{ ...commonBtnStyle, color: 'var(--fx-text-secondary)' }}>
            Cancel
          </Button>
          <Button
            onClick={confirmClear}
            variant="contained"
            disableElevation
            sx={{
              ...commonBtnStyle,
              bgcolor: confirmCopy.color,
              '&:hover': { bgcolor: confirmCopy.color, filter: 'brightness(0.92)' },
            }}
          >
            {confirmCopy.action}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TrainingControlPanel;
